import { getBlobLink, getMedia, idb, isRemoteRef, listen, upload } from "@root/persist";
import { b64Encoder, download, renderMD, setSelectOptions } from "@root/utils";
import { placeholderView } from "@views/common";
import { makeScenarioCardView } from "@views/scenario-card";
import { RampikeModal } from "@rampike/modal";
import { start } from "./chat/start";
import { ScenarioCard } from "@root/types";
import { RampikeFilePicker } from "@rampike/filepicker";
import { importSTMessages } from "./library/st";
import { downloadScenarioCard } from "./library/dl";
import { toast } from "./toasts";
import { startArmory } from "./library/armory";
import { DatacatCard, importDatacatJSON } from "./library/datacat";
import { RampikePagination } from "@rampike/pagination";
import { filterBySearch } from "./library/search";
import { hashListenerUnit } from "./library/hash";

const CARDS_PER_PAGE = 16;
let openerRelay: {
	scenarioId: string
} | null = null;

export function libraryUnit() {
	hashListenerUnit();

	const startButton        = document.querySelector<HTMLButtonElement>("#library-start-button")!;
	const startPersonaPicker = document.querySelector<HTMLSelectElement>("#library-start-persona")!;
	const startImportButton  = document.querySelector<RampikeFilePicker>("#library-start-import")!;
	const importButton       = document.querySelector<RampikeFilePicker>("#library-import")!;
	const downloadButton     = document.querySelector<HTMLButtonElement>("#library-download")!;
	const armoryButton       = document.querySelector<HTMLButtonElement>("#library-armory-button")!;
	const pager              = document.querySelector<RampikePagination>("#library-pager")!;
	const modal              = document.querySelector<RampikeModal>("#library-start")!;
	const search = {
		open: document.querySelector<HTMLButtonElement>("#library-search")!,
		button: document.querySelector<HTMLButtonElement>("#library-search-button")!,
		cancel: document.querySelector<HTMLButtonElement>("#library-search-cancel")!,
		dialog: document.querySelector<HTMLElement>("#library-search-dialog")!
	}

	startButton.addEventListener("click", async () => {
		if (!openerRelay) return;
		const personaId = startPersonaPicker.value;
		if (!personaId) return;
		await start(personaId, openerRelay.scenarioId);
		modal.close();
	});
	startImportButton.addEventListener("input", async () => {
		const file = startImportButton.input.files?.[0];
		if (!file) return;
		const personaId = startPersonaPicker.value;
		if (!personaId) return;
		if (!openerRelay) return;

		const messages = await importSTMessages(file);
		if (messages.length === 0) return;

		await start(personaId, openerRelay.scenarioId, messages);
		modal.close();
	});

	importButton.addEventListener("input", () => {
		const files = importButton.input.files;
		if (!files?.[0]) return;

		for (let i = 0; i < files.length; ++i) {
			importScenario(files.item(i)!);
		}
	});

	downloadButton.addEventListener("click", async () => {
		alert([
			"Apologies, download is not available. jannyai.com",
			" started using Cloudflare's bot protection and",
			" can't be accessed automatically anymore.\n\n",
			"You still can import JSON files downloaded from",
			" https://datacat.run/ \n\n",
			"There's a chance a better integration will be",
			" implemented soon"
		].join(""));
		return;
		// const url = prompt("Enter the link to a character page on janitorai.com")?.trim();
		// if (!url) return;
		// const card = await downloadScenarioCard(url);
		// if (card) {
		// 	await idb.set("scenarios", card);
		// 	toast(
		// 		"scenario downloaded!\ndon't forget to add the character name and check the definition",
		// 		{
		// 			actions: [
		// 				["ok", close => close()],
		// 				["open", close => {
		// 					close();
		// 					window.open(`#scenario-editor.${card.id}`);
		// 				}]
		// 			]
		// 		}
		// 	);
		// }
	});
	armoryButton.addEventListener("click", startArmory);

	search.open.addEventListener("click", () => {
		search.dialog.hidden = false;
	});
	search.button.addEventListener("click", () => {
		pager.page = 0;
		search.dialog.dataset.active = "true";
		update();
	});
	search.cancel.addEventListener("click", () => {
		search.dialog.hidden = true;
		search.dialog.removeAttribute("data-active");
		update();
	});

	pager.addEventListener("pick", update);
	listen(async u => {
		if (u.storage !== "idb") return;
		if (u.store !== "scenarios") return;

		update();
	});
	update();
}

async function update() {
	const list = document.querySelector<HTMLElement>("#library-cards")!;
	const pager = document.querySelector<RampikePagination>("#library-pager")!;

	list.innerHTML = "";
	const items = await idb.getAll("scenarios");
	if (!items.success) return;

	items.value = filterBySearch(items.value);

	pager.pageCount = Math.ceil(items.value.length / CARDS_PER_PAGE);
	pager.style.display = pager.pageCount <= 1 ? "none" : "flex";
	if (pager.page >= pager.pageCount) pager.page = pager.pageCount - 1;
	if (pager.pageCount > 0 && pager.page < 0) pager.page = 0;

	const ix0 = pager.page * CARDS_PER_PAGE;
	const ix1 = ix0 + CARDS_PER_PAGE;
	const contents = items.value.reverse().slice(ix0, ix1).map(scenarioCardView);

	list.append(...contents);
	if (contents.length === 0)
		list.append(placeholderView("No scenario cards found"));
}

function scenarioCardView(item: ScenarioCard) {
	return makeScenarioCardView(
		item,
		item.card.picture ? getBlobLink(item.card.picture) : null,
		{
			play:     () => openStartModal(item.id, item.card.description),
			download: () => downloadScenario(item),
			delete:   () => deleteScenario(item.id, item.card.title),
			edit:     () => { document.location.hash = `scenario-editor.${item.id}`; }
		}
	);
}

async function downloadScenario(card: ScenarioCard) {
	const payload = { ...card, card: { ...card.card }, chat: { ...card.chat } };

	async function encoded(value: string | null) {
		if (!value) return null;
		const icon = await getMedia(value);
		if (icon)
			return await b64Encoder.encode(icon.media);
		// a remote picture that can't be fetched (offline, CORS) is still worth keeping as a link
		return isRemoteRef(value) ? value : null;
	}

	payload.card.picture = await encoded(card.card.picture);
	payload.chat.picture = await encoded(card.chat.picture);

	const filename = [payload.card.title, payload.chat.name].filter(t => t).join(" | ");
	download(JSON.stringify(payload), `${filename}.aegir.scenario.json`);
}

export async function importScenario(file: Blob) {
	const raw = await file.text();
	const parsed = JSON.parse(raw) as ScenarioCard | DatacatCard;

	if ("spec" in parsed && parsed.spec === "chara_card_v2") {
		const converted = await importDatacatJSON(parsed);
		idb.set("scenarios", converted);
	}
	if ("id" in parsed){
		async function decode(b64: string | null) {
			if (!b64) return null;
			if (isRemoteRef(b64)) return b64;
			const file = await b64Encoder.decode(b64);
			const media = await upload(file);
			return media;
		}
		parsed.card.picture = await decode(parsed.card.picture);
		parsed.chat.picture = await decode(parsed.chat.picture);
		parsed.lastUpdate = Date.now();
	
		idb.set("scenarios", parsed);
	}
}

function deleteScenario(id: string, name: string) {
	const ok = confirm(`scenario "${name}" will be deleted`);
	if (!ok) return;

	idb.del("scenarios", id);
}

async function openStartModal(scenario: string, descriptionMD: string) {
	const modal = document.querySelector<RampikeModal>("#library-start")!;
	const picker = modal.querySelector<HTMLSelectElement>("#library-start-persona")!;
	const description = document.querySelector<HTMLElement>("#library-start-description")!;
	const placeholder = modal.querySelector<HTMLElement>("#library-start-placeholder")!;
	const noPlaceholder = modal.querySelector<HTMLElement>("#library-start-no-placeholder")!;

	const personas = await idb.getAll("personas");
	if (!personas.success) return;

	placeholder.hidden = personas.value.length > 0;
	noPlaceholder.style.display = placeholder.hidden ? "contents" : "none";

	setSelectOptions(
		picker,
		personas.value.map(({id, name}) => [id, name]),
		personas.value[0]?.id
	);

	openerRelay = {
		scenarioId: scenario
	};

	description.hidden = descriptionMD.trim() ? false : true;
	description.innerHTML = renderMD(descriptionMD);

	modal.open();
}
